import { useState, useEffect, useRef } from 'react';
import { formatDateNL } from '../utils/dateFormat';
import { axiosInstance } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, Trash2, FileText, Edit, Eye, Printer, Download, X, RotateCcw, Mail, Clock, Check, Sparkles, Euro, Star, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import html2pdf from 'html2pdf.js';
import EmailSendModal from './EmailSendModal';
import { StatusActionDropdown } from './StatusActionDropdown';
import { PaymentPlanEditor, PaymentPlanBlock } from './PaymentPlan';
import { StandardTermsBlock } from './StandardTerms';
import EmailHistoryModal from './EmailHistoryModal';
import ImportDocumentModal from './ImportDocumentModal';
import { ReminderBadge, ReminderMenu, ReminderSettingsBar } from './InvoiceReminders';
import ExternalInvoices from './ExternalInvoices';

const COMPANY = {
  name: 'Cardoso Bouw En Onderhoud',
  address: 'Vuntuslaan 69',
  city: '1231 NR Loosdrecht',
  phone: '0685071009',
  email: 'info@cardosobouw.nl',
  kvk: '85310646',
  btw_id: 'NL004077138B58',
  iban: 'NL33INGB0709828160',
  bic: 'INGBNL2A',
};

const emptyItem = { description: '', quantity: 1, unit: '', unit_price: 0, discount: 0, btw: 21 };

const Invoices = ({ user }) => {
  const readOnly = user?.role === 'boekhouder';
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const [clientAddresses, setClientAddresses] = useState([]);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailHistoryInvoice, setEmailHistoryInvoice] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [reviewInvoice, setReviewInvoice] = useState(null);
  const [reviewUrl, setReviewUrl] = useState('');
  const [reviewAuto, setReviewAuto] = useState(true);
  const [reviewSending, setReviewSending] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const printRef = useRef(null);

  const handleOpenReview = async (inv) => {
    setReviewInvoice(inv);
    try {
      const r = await axiosInstance.get('/settings/google-review');
      setReviewUrl(r.data.url || '');
      setReviewAuto(r.data.auto !== false);
    } catch { setReviewUrl(''); }
  };

  const saveReviewSettings = () => axiosInstance.put('/settings/google-review', { url: reviewUrl.trim(), auto: reviewAuto });

  const handleSaveReviewOnly = async () => {
    if (!reviewUrl.trim()) { toast.error('Vul eerst je Google review-link in'); return; }
    try {
      await saveReviewSettings();
      toast.success('Review-instellingen opgeslagen');
      setReviewInvoice(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Opslaan mislukt'); }
  };

  const handleSendReview = async () => {
    if (!reviewUrl.trim()) { toast.error('Vul eerst je Google review-link in'); return; }
    setReviewSending(true);
    try {
      await saveReviewSettings();
      const r = await axiosInstance.post(`/invoices/${reviewInvoice.id}/request-review`);
      toast.success(`Review-verzoek verstuurd naar ${r.data.sent_to}`);
      setReviewInvoice(null);
      fetchInvoices();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Versturen mislukt');
    } finally {
      setReviewSending(false);
    }
  };

  const [form, setForm] = useState({
    client_name: '', _client_id: '', client_address: '', client_city: '',
    client_email: '', client_phone: '',
    description: '',
    btw_status: 'normal',
    invoice_date: new Date().toISOString().slice(0, 10),
    due_date: '',
    items: [{ ...emptyItem }],
    general_discount: 0,
    notes: '',
    down_payment_label: '',
    payment_plan: null,
  });

  useEffect(() => { fetchInvoices(); fetchClients(); }, []);

  const fetchInvoices = async () => {
    try { setLoading(true); const res = await axiosInstance.get('/invoices'); setInvoices(res.data); }
    catch { toast.error('Fout bij laden facturen'); }
    finally { setLoading(false); }
  };

  const fetchClients = async () => {
    try { const res = await axiosInstance.get('/clients/all'); setClients(res.data); } catch {}
  };

  const handleClientChange = (name) => {
    const client = clients.find(c => c.name === name);
    const addrs = client?.addresses?.map(a => {
      const full = (a.address || a.street || '').trim();
      const idx = full.lastIndexOf(',');
      const street = idx > -1 ? full.slice(0, idx).trim() : full;
      const city = idx > -1 ? full.slice(idx + 1).trim() : (a.city || '');
      return { id: a.id, label: a.label || '', email: a.email || '', phone: a.phone || '', full, street, city };
    }) || [];
    setClientAddresses(addrs);
    setForm(f => ({
      ...f,
      _client_id: name,
      client_name: name,
      client_address: '',
      client_city: '',
      client_email: '',
      client_phone: '',
    }));
  };

  const handleAddressChange = (addrId) => {
    const addr = clientAddresses.find(a => a.id === addrId);
    if (!addr) return;
    setForm(f => ({
      ...f,
      client_name: addr.label ? addr.label : f.client_name,
      client_address: addr.street,
      client_city: addr.city || f.client_city,
      client_email: addr.email || f.client_email,
      client_phone: addr.phone || f.client_phone,
      _selected_addr_id: addrId,
    }));
  };

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { ...emptyItem }] }));
  const removeItem = (i) => setForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const updateItem = (i, field, value) => {
    setForm(f => {
      const items = [...f.items];
      items[i] = { ...items[i], [field]: value };
      return { ...f, items };
    });
  };

  const calcLineTotal = (item) => {
    const lt = (item.quantity || 0) * (item.unit_price || 0);
    const d = parseFloat(item.discount) || 0;
    return d > 0 ? lt * (1 - d / 100) : lt;
  };
  const calcSubtotal = () => form.items.reduce((s, i) => s + calcLineTotal(i), 0);
  const calcBtw = () => {
    if ((form.btw_status || 'normal').toLowerCase() === 'verlegd') return 0;
    return form.items.reduce((s, i) => s + calcLineTotal(i) * (i.btw / 100), 0);
  };

  const handleSubmit = async () => {
    if (!form.client_name) { toast.error('Selecteer een klant'); return; }
    if (!form.items.length) { toast.error('Voeg minimaal een regel toe'); return; }

    const payload = {
      ...form,
      invoice_date: form.invoice_date ? formatDateNL(form.invoice_date) : '',
      due_date: form.due_date ? formatDateNL(form.due_date) : '',
      down_payment_label: (form.down_payment_label || '').trim() || null,
      general_discount: parseFloat(form.general_discount) || 0,
      items: form.items.map(i => ({
        ...i, quantity: parseFloat(i.quantity) || 0, unit_price: parseFloat(i.unit_price) || 0,
        discount: parseFloat(i.discount) || 0,
        btw: parseFloat(i.btw) || 0,
      })),
    };

    try {
      if (editingId) {
        await axiosInstance.put(`/invoices/${editingId}`, payload);
        toast.success('Factuur bijgewerkt!');
      } else {
        await axiosInstance.post('/invoices', payload);
        toast.success('Factuur aangemaakt!');
      }
      resetForm();
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fout bij opslaan factuur');
    }
  };

  const resetForm = () => {
    setShowForm(false); setEditingId(null); setClientAddresses([]);
    setForm({ client_name: '', _client_id: '', client_address: '', client_city: '', client_email: '', client_phone: '', description: '', btw_status: 'normal',
      invoice_date: new Date().toISOString().slice(0, 10), due_date: '',
      items: [{ ...emptyItem }], general_discount: 0, notes: '', down_payment_label: '', payment_plan: null });
  };

  const handleEdit = (inv) => {
    setEditingId(inv.id);
    const client = clients.find(c => c.name === inv.client_name);
    setClientAddresses(client?.addresses?.map(a => {
      const full = (a.address || a.street || '').trim();
      const idx = full.lastIndexOf(',');
      const street = idx > -1 ? full.slice(0, idx).trim() : full;
      const city = idx > -1 ? full.slice(idx + 1).trim() : (a.city || '');
      return { id: a.id, label: a.label || '', email: a.email || '', phone: a.phone || '', full, street, city };
    }) || []);
    const toDateInput = (d) => {
      if (!d) return '';
      const parts = d.split('-');
      if (parts[0].length === 4) return d;
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    };
    setForm({
      client_name: inv.client_name, _client_id: '', client_address: inv.client_address, client_city: inv.client_city,
      client_email: inv.client_email || '', client_phone: inv.client_phone || '',
      description: inv.description,
      btw_status: inv.btw_status || 'normal',
      invoice_date: toDateInput(inv.invoice_date), due_date: toDateInput(inv.due_date),
      items: inv.items?.length ? inv.items.map(i => ({ description: i.description, quantity: i.quantity, unit: i.unit || '', unit_price: i.unit_price, discount: i.discount || 0, btw: i.btw || 21 })) : [{ ...emptyItem }],
      general_discount: inv.general_discount || 0,
      notes: inv.notes || '',
      down_payment_label: inv.down_payment_label || '',
      payment_plan: inv.payment_plan || null,
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deze factuur verwijderen?')) return;
    try { await axiosInstance.delete(`/invoices/${id}`); toast.success('Factuur verwijderd'); fetchInvoices(); }
    catch { toast.error('Fout bij verwijderen factuur'); }
  };

  const handleMarkPaid = async (inv) => {
    if (inv.status === 'betaald') {
      toast.info('Deze factuur is al gemarkeerd als betaald');
      return;
    }
    if (!window.confirm(`Factuur ${inv.number} markeren als BETAALD?\n\nKlant: ${inv.client_name}\nBedrag: € ${(inv.total_incl ?? inv.total ?? 0).toFixed(2)}`)) return;
    try {
      await axiosInstance.patch(`/invoices/${inv.id}/status`, { status: 'betaald' });
      toast.success(`Factuur ${inv.number} gemarkeerd als betaald`);
      fetchInvoices();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Wijzigen mislukt');
    }
  };

  const handleCreateCredit = async (inv) => {
    if (inv.type === 'credit') {
      toast.error('Dit is al een creditfactuur');
      return;
    }
    if (inv.credited_by) {
      if (!window.confirm(`Deze factuur is al gecrediteerd door ${inv.credited_by}. Nog een creditfactuur maken?`)) return;
    } else {
      if (!window.confirm(`Creditfactuur maken voor ${inv.number}?`)) return;
    }
    try {
      const res = await axiosInstance.post(`/invoices/${inv.id}/credit`);
      toast.success(`Creditfactuur ${res.data.number} aangemaakt!`);
      fetchInvoices();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fout bij maken creditfactuur'); }
  };

  const handleTestPaidNotification = async (inv) => {
    if (!window.confirm(`Test "Factuur betaald" notificatie versturen voor ${inv.number}?\n\nEr wordt een test-e-mail gestuurd naar uw beheer-inbox (info@cardosobouw.nl) — alsof de klant zojuist via Mollie iDEAL heeft betaald.`)) return;
    try {
      const res = await axiosInstance.post(`/invoices/${inv.id}/test-paid-notification`);
      toast.success(`Test-bevestiging verzonden naar ${res.data.sent_to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test-notificatie mislukt');
    }
  };

  const handleTestKlantBedankt = async (inv) => {
    const klantEmail = inv.client_email || '';
    if (!klantEmail) {
      toast.error('Deze factuur heeft geen klant-e-mailadres');
      return;
    }
    if (!window.confirm(`Test "Bedankt voor uw betaling" e-mail versturen naar ${klantEmail}?\n\nDe klant ontvangt een bedankje + Google-review knop + link naar de factuur. Gebruik dit voor preview.`)) return;
    try {
      const res = await axiosInstance.post(`/invoices/${inv.id}/test-klant-bedankt-mail`);
      toast.success(`Bedankt-mail verzonden naar ${res.data.sent_to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bedankt-mail mislukt');
    }
  };

  const handlePrint = () => {
    const w = window.open('', '_blank');
    w.document.write(printRef.current.innerHTML);
    w.document.write('<style>@media print { body { margin: 0; } } body { font-family: Arial, sans-serif; }</style>');
    w.document.close();
    w.print();
  };

  const handleDownloadPdf = () => {
    if (!printRef.current || !previewInvoice) return;
    const filename = `Factuur_${previewInvoice.number}_${(previewInvoice.client_name || '').replace(/[^a-z0-9]/gi, '_')}.pdf`;
    const opts = {
      margin: 0,
      filename,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      pagebreak: { mode: ['css', 'legacy'], before: '.html2pdf__page-break' },
    };
    html2pdf().set(opts).from(printRef.current).save().then(() => toast.success('PDF gedownload')).catch(() => toast.error('Fout bij downloaden PDF'));
  };

  const handleSyncPayment = async (inv) => {
    if (!inv.mollie_payment?.payment_id) {
      toast.error('Geen Mollie-betaling gekoppeld');
      return;
    }
    try {
      const r = await axiosInstance.post(`/invoices/${inv.id}/sync-payment-status`);
      toast.success(`Status bijgewerkt: ${r.data.status}`);
      fetchInvoices();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fout bij synchroniseren');
    }
  };

  const handleOpenEmail = (inv) => {
    setPreviewInvoice(inv);
    setTimeout(() => setEmailModalOpen(true), 100);
  };

  const fmtEuro = (v) => `€ ${(Number(v) || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const getInvoiceTotals = (inv) => {
    if (!inv) return { subtotal: 0, btw_totals: {}, total_btw: 0, total_incl: 0, general_discount: 0, is_verlegd: false };
    const is_verlegd = (inv.btw_status || '').toLowerCase() === 'verlegd';
    const general_discount = inv.general_discount || 0;
    if (inv.btw_totals && Object.keys(inv.btw_totals).length > 0 && !is_verlegd) {
      return { subtotal: inv.subtotal || 0, btw_totals: inv.btw_totals, total_btw: inv.total_btw || 0, total_incl: inv.total_incl || 0, general_discount, is_verlegd };
    }
    let subtotal = 0;
    const btw_totals = {};
    (inv.items || []).forEach(it => {
      let lt;
      if (it.line_total != null) {
        lt = it.line_total;
      } else {
        lt = (it.quantity || 0) * (it.unit_price || 0);
        const d = parseFloat(it.discount) || 0;
        if (d > 0) lt *= (1 - d / 100);
      }
      subtotal += lt;
      if (is_verlegd) return;
      const rate = parseFloat(it.btw) || 0;
      if (rate > 0) {
        const amt = lt * rate / 100;
        const key = `${rate}%`;
        btw_totals[key] = (btw_totals[key] || 0) + amt;
      }
    });
    const total_btw = is_verlegd ? 0 : Object.values(btw_totals).reduce((a, b) => a + b, 0);
    return { subtotal, btw_totals, total_btw, total_incl: subtotal - general_discount + total_btw, general_discount, is_verlegd };
  };

  const [tab, setTab] = useState('eigen');
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-4xl font-bold text-gray-800">Facturen</h1>
        {readOnly ? (
          <span className="text-xs px-3 py-1.5 rounded-full bg-[#1a5276]/10 text-[#1a5276] font-medium" data-testid="readonly-badge">Alleen lezen — bekijken en downloaden</span>
        ) : (
        <div className="flex gap-2">
          <Button onClick={() => setImportOpen(true)} variant="outline" className="border-[#3da5a5] text-[#3da5a5] hover:bg-[#3da5a5]/10" data-testid="import-invoice-btn">
            <Sparkles className="w-4 h-4 mr-2" /> Importeer (IA)
          </Button>
          <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-[#3da5a5] hover:bg-[#2d8585]" data-testid="new-invoice-btn">
            <Plus className="w-4 h-4 mr-2" /> Nieuwe Factuur
          </Button>
        </div>
        )}
      </div>
      <div className="flex gap-1 border-b border-gray-200" data-testid="invoice-tabs">
        <button type="button" onClick={() => setTab('eigen')} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === 'eigen' ? 'border-[#3da5a5] text-[#1a5276]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-testid="tab-eigen-facturen">Eigen facturen</button>
        <button type="button" onClick={() => setTab('derden')} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === 'derden' ? 'border-[#3da5a5] text-[#1a5276]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-testid="tab-facturen-derden">Facturen van derden</button>
      </div>
      {tab === 'derden' ? <ExternalInvoices readOnly={readOnly} /> : (<>
      {!readOnly && <ReminderSettingsBar />}

      {/* FORM */}
      {showForm && (
        <Card className="bg-white shadow-sm border-gray-200" data-testid="invoice-form">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-gray-800">{editingId ? 'Factuur bewerken' : 'Nieuwe Factuur'}</CardTitle>
            <Button variant="ghost" size="sm" onClick={resetForm}><X className="w-4 h-4" /></Button>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-700 text-xs text-gray-500">Klant laden (optioneel)</Label>
                <Select value={form._client_id || ''} onValueChange={handleClientChange}>
                  <SelectTrigger className="bg-gray-50 border-gray-200" data-testid="invoice-client-select"><SelectValue placeholder="Kies een opgeslagen klant..." /></SelectTrigger>
                  <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {clientAddresses.length > 0 && (
                <div>
                  <Label className="text-gray-700 text-xs text-gray-500">Adres laden (optioneel)</Label>
                  <Select value={form._selected_addr_id || ''} onValueChange={handleAddressChange}>
                    <SelectTrigger className="bg-gray-50 border-gray-200" data-testid="invoice-address-select"><SelectValue placeholder="Kies een opgeslagen adres..." /></SelectTrigger>
                    <SelectContent>{clientAddresses.map(a => <SelectItem key={a.id} value={a.id}>{a.label ? `${a.label} — ${a.full}` : a.full}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-gray-700">Naam Klant *</Label>
                <Input value={form.client_name} onChange={e => setForm(f => ({ ...f, client_name: e.target.value }))} placeholder="Naam invoeren" className="bg-white border-gray-200" data-testid="invoice-client-name-input" />
              </div>
              <div>
                <Label className="text-gray-700">Adres</Label>
                <Input value={form.client_address} onChange={e => setForm(f => ({ ...f, client_address: e.target.value }))} placeholder="Adres invoeren" className="bg-white border-gray-200" data-testid="invoice-client-address-input" />
              </div>
              <div>
                <Label className="text-gray-700">Plaats</Label>
                <Input value={form.client_city} onChange={e => setForm(f => ({ ...f, client_city: e.target.value }))} placeholder="Plaats invoeren" className="bg-white border-gray-200" data-testid="invoice-client-city-input" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-700">Email</Label>
                <Input type="email" value={form.client_email} onChange={e => setForm(f => ({ ...f, client_email: e.target.value }))} placeholder="klant@email.com" className="bg-white border-gray-200" data-testid="invoice-client-email-input" />
              </div>
              <div>
                <Label className="text-gray-700">Telefoon</Label>
                <Input value={form.client_phone} onChange={e => setForm(f => ({ ...f, client_phone: e.target.value }))} placeholder="+31 ..." className="bg-white border-gray-200" data-testid="invoice-client-phone-input" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div><Label className="text-gray-700">Omschrijving</Label><Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="bg-gray-50 border-gray-200" /></div>
              <div><Label className="text-gray-700">Factuurdatum</Label><Input type="date" value={form.invoice_date} onChange={e => setForm(f => ({ ...f, invoice_date: e.target.value }))} className="bg-gray-50 border-gray-200" /></div>
              <div><Label className="text-gray-700">Vervaldatum</Label><Input type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} className="bg-gray-50 border-gray-200" /></div>
            </div>

            {/* BTW status selector — normal vs verlegd */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <Label className="text-gray-700 font-semibold flex items-center gap-2">
                <span>📋 BTW-regeling</span>
              </Label>
              <div className="flex flex-wrap gap-3 mt-2">
                <label className={`flex items-center gap-2 px-3 py-2 rounded border cursor-pointer transition-colors ${(form.btw_status || 'normal') !== 'verlegd' ? 'bg-white border-[#3da5a5] ring-2 ring-[#3da5a5]/20' : 'bg-gray-50 border-gray-200'}`}>
                  <input
                    type="radio"
                    name="btw_status_invoice"
                    value="normal"
                    checked={(form.btw_status || 'normal') !== 'verlegd'}
                    onChange={() => setForm(f => ({ ...f, btw_status: 'normal' }))}
                    data-testid="invoice-btw-status-normal"
                  />
                  <span className="text-sm">Normaal — BTW per regel (0% / 9% / 21%)</span>
                </label>
                <label className={`flex items-center gap-2 px-3 py-2 rounded border cursor-pointer transition-colors ${form.btw_status === 'verlegd' ? 'bg-white border-[#3da5a5] ring-2 ring-[#3da5a5]/20' : 'bg-gray-50 border-gray-200'}`}>
                  <input
                    type="radio"
                    name="btw_status_invoice"
                    value="verlegd"
                    checked={form.btw_status === 'verlegd'}
                    onChange={() => setForm(f => ({ ...f, btw_status: 'verlegd' }))}
                    data-testid="invoice-btw-status-verlegd"
                  />
                  <span className="text-sm font-medium text-amber-900">BTW verlegd — afnemer is btw-plichtig</span>
                </label>
              </div>
              {form.btw_status === 'verlegd' && (
                <p className="text-xs text-amber-700 mt-2 italic">⚠️ Bij BTW verlegd wordt geen btw berekend; de afnemer is verantwoordelijk voor de btw-afdracht (omgekeerde heffing). Vermelding op factuur: "BTW verlegd".</p>
              )}
            </div>

            {/* Line Items */}
            <div>
              <Label className="text-gray-700 font-semibold">Regels</Label>
              <div className="space-y-2 mt-2">
                {form.items.map((item, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-end bg-gray-50 p-3 rounded-lg">
                    <div className="col-span-4"><Label className="text-xs text-gray-500">Omschrijving</Label><Input value={item.description} onChange={e => updateItem(i, 'description', e.target.value)} className="bg-white border-gray-200 text-sm" /></div>
                    <div className="col-span-1"><Label className="text-xs text-gray-500">Aantal</Label><Input type="number" step="0.01" value={item.quantity} onChange={e => updateItem(i, 'quantity', e.target.value)} className="bg-white border-gray-200 text-sm" /></div>
                    <div className="col-span-1"><Label className="text-xs text-gray-500">Bedrag</Label><Input type="number" step="0.01" value={item.unit_price} onChange={e => updateItem(i, 'unit_price', e.target.value)} className="bg-white border-gray-200 text-sm" /></div>
                    <div className="col-span-1"><Label className="text-xs text-gray-500">Korting %</Label><Input type="number" step="0.01" min="0" max="100" value={item.discount || 0} onChange={e => updateItem(i, 'discount', e.target.value)} className="bg-white border-gray-200 text-sm" data-testid={`invoice-item-discount-${i}`} /></div>
                    <div className="col-span-1"><Label className="text-xs text-gray-500">BTW %</Label><Input type="number" value={item.btw} onChange={e => updateItem(i, 'btw', e.target.value)} className="bg-white border-gray-200 text-sm" /></div>
                    <div className="col-span-1"><Label className="text-xs text-gray-500">Eenheid</Label><Input value={item.unit} onChange={e => updateItem(i, 'unit', e.target.value)} placeholder="uren" className="bg-white border-gray-200 text-sm" /></div>
                    <div className="col-span-2 text-right font-semibold text-gray-800 text-sm pt-5">{fmtEuro(calcLineTotal(item))}</div>
                    <div className="col-span-1 flex justify-end"><Button variant="ghost" size="sm" onClick={() => removeItem(i)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button></div>
                  </div>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={addItem} className="mt-2 border-gray-300 text-gray-700"><Plus className="w-4 h-4 mr-1" /> Regel toevoegen</Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div><Label className="text-gray-700">Korting (€)</Label><Input type="number" step="0.01" value={form.general_discount} onChange={e => setForm(f => ({ ...f, general_discount: e.target.value }))} className="bg-gray-50 border-gray-200" data-testid="invoice-general-discount" /></div>
              <div><Label className="text-gray-700">Opmerkingen</Label><textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="w-full bg-gray-50 border border-gray-200 rounded-lg p-2 text-sm text-gray-800 h-40" /></div>
              <div>
                <Label className="text-gray-700">Naam betaling <span className="text-xs text-gray-400">(optioneel)</span></Label>
                <Input
                  type="text"
                  value={form.down_payment_label}
                  onChange={e => setForm(f => ({ ...f, down_payment_label: e.target.value }))}
                  placeholder="Aanbetaling"
                  className="bg-gray-50 border-gray-200"
                  data-testid="invoice-down-payment-label-input"
                />
                <p className="text-[11px] text-gray-400 mt-1">bijv. Aanbetaling, Tussenbetaling, Eindbetaling — verschijnt op PDF & klantportaal</p>
              </div>
            </div>

            <PaymentPlanEditor value={form.payment_plan} onChange={(pp) => setForm(f => ({ ...f, payment_plan: pp }))} totalIncl={calcSubtotal() - (parseFloat(form.general_discount) || 0) + calcBtw()} />

            <div className="flex items-center justify-between border-t border-gray-200 pt-4">
              <div className="text-lg font-bold text-gray-800">
                Subtotaal: {fmtEuro(calcSubtotal())}
                {(parseFloat(form.general_discount) || 0) > 0 && <> | Korting: <span className="text-red-600">-{fmtEuro(parseFloat(form.general_discount))}</span></>}
                {' | '}BTW: {fmtEuro(calcBtw())} | <span className="text-[#3da5a5]">Totaal: {fmtEuro(calcSubtotal() - (parseFloat(form.general_discount) || 0) + calcBtw())}</span>
              </div>
              <Button onClick={handleSubmit} className="bg-[#3da5a5] hover:bg-[#2d8585]" data-testid="save-invoice-btn">{editingId ? 'Bijwerken' : 'Factuur maken'}</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* PREVIEW */}
      {previewInvoice && (
        <Card className="bg-white shadow-sm border-gray-200" data-testid="invoice-preview">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-gray-800">{previewInvoice.type === 'credit' ? 'Creditfactuur' : 'Factuur'} {previewInvoice.number}</CardTitle>
            <div className="flex gap-2">
              {!readOnly && <Button size="sm" onClick={() => setEmailModalOpen(true)} className="bg-[#1a5276] hover:bg-[#143d5a]" data-testid="email-invoice-from-preview"><Mail className="w-4 h-4 mr-1" /> Versturen</Button>}
              <Button size="sm" onClick={handleDownloadPdf} className="bg-[#3da5a5] hover:bg-[#2d8585]" data-testid="download-invoice-pdf"><Download className="w-4 h-4 mr-1" /> PDF downloaden</Button>
              <Button size="sm" onClick={handlePrint} className="bg-blue-600 hover:bg-blue-700"><Printer className="w-4 h-4 mr-1" /> Afdrukken</Button>
              <Button variant="ghost" size="sm" onClick={() => setPreviewInvoice(null)}><X className="w-4 h-4" /></Button>
            </div>
          </CardHeader>
          <CardContent>
            <div ref={printRef}>
              <div style={{ fontFamily: 'Arial, sans-serif', padding: '40px', maxWidth: '800px', margin: '0 auto', color: '#333' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '30px' }}>
                  <div>
                    <h2 style={{ color: previewInvoice.type === 'credit' ? '#c0392b' : '#1a5276', fontSize: '22px', margin: '0 0 20px 0' }}>
                      {previewInvoice.type === 'credit' ? 'Creditfactuur' : 'Factuur'} {previewInvoice.number}
                    </h2>
                    {previewInvoice.credits_invoice && <p style={{ margin: '2px 0', fontSize: '13px', color: '#c0392b' }}><strong>Credit voor factuur:</strong> {previewInvoice.credits_invoice}</p>}
                    <p style={{ margin: '2px 0', fontSize: '13px' }}><strong>Naam Klant:</strong> {previewInvoice.client_name}</p>
                    <p style={{ margin: '2px 0', fontSize: '13px' }}><strong>Adres Klant:</strong> {previewInvoice.client_address}</p>
                    <p style={{ margin: '2px 0', fontSize: '13px' }}><strong>Plaats Klant:</strong> {previewInvoice.client_city}</p>
                    {previewInvoice.client_email && <p style={{ margin: '2px 0', fontSize: '13px' }}><strong>E-mail:</strong> {previewInvoice.client_email}</p>}
                    {previewInvoice.client_phone && <p style={{ margin: '2px 0', fontSize: '13px' }}><strong>Telefoon:</strong> {previewInvoice.client_phone}</p>}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <img src="/logo-cardoso.jpeg" alt="Logo" style={{ width: '120px', marginBottom: '10px' }} />
                    <p style={{ margin: '2px 0', fontSize: '12px' }}>{COMPANY.name}</p>
                    <p style={{ margin: '2px 0', fontSize: '12px' }}>{COMPANY.address}</p>
                    <p style={{ margin: '2px 0', fontSize: '12px' }}>{COMPANY.city}</p>
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', padding: '10px', background: '#f8f9fa', borderRadius: '4px' }}>
                  <p style={{ margin: '2px 0', fontSize: '13px' }}><strong>Omschrijving factuur:</strong> {previewInvoice.description}</p>
                  <div style={{ textAlign: 'right' }}><p style={{ margin: '2px 0', fontSize: '13px' }}><strong>Factuurdatum:</strong> {previewInvoice.invoice_date}</p><p style={{ margin: '2px 0', fontSize: '13px' }}><strong>Vervaldatum:</strong> {previewInvoice.due_date}</p></div>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', marginBottom: '20px' }}>
                  <thead><tr style={{ background: '#1a5276', color: 'white' }}>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Omschrijving</th>
                    <th style={{ padding: '8px', textAlign: 'right' }}>Aantal</th>
                    <th style={{ padding: '8px', textAlign: 'right' }}>Bedrag</th>
                    {previewInvoice.items?.some(it => (it.discount || 0) > 0) && <th style={{ padding: '8px', textAlign: 'right' }}>Korting</th>}
                    <th style={{ padding: '8px', textAlign: 'right' }}>BTW</th>
                    <th style={{ padding: '8px', textAlign: 'right' }}>Totaal excl. BTW</th>
                  </tr></thead>
                  <tbody>
                    {previewInvoice.items?.map((item, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '8px' }}>{item.description}</td>
                        <td style={{ padding: '8px', textAlign: 'right' }}>{item.quantity} {item.unit}</td>
                        <td style={{ padding: '8px', textAlign: 'right' }}>{fmtEuro(item.unit_price)}</td>
                        {previewInvoice.items?.some(it => (it.discount || 0) > 0) && <td style={{ padding: '8px', textAlign: 'right', color: (item.discount || 0) > 0 ? '#dc2626' : '#9ca3af' }}>{(item.discount || 0) > 0 ? `${item.discount}%` : '-'}</td>}
                        <td style={{ padding: '8px', textAlign: 'right' }}>{item.btw}%</td>
                        <td style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold' }}>{fmtEuro(item.line_total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
                  <div style={{ width: '280px' }}>
                    {(() => { const t = getInvoiceTotals(previewInvoice); return (<>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '13px' }}><span>Subtotaal</span><strong>{fmtEuro(t.subtotal)}</strong></div>
                    {t.general_discount > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '13px', color: '#dc2626' }}><span>Korting</span><span>-{fmtEuro(t.general_discount)}</span></div>
                    )}
                    {!t.is_verlegd && Object.entries(t.btw_totals || {}).map(([rate, amt]) => (
                      <div key={rate} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '13px' }}><span>BTW {rate}</span><span>{fmtEuro(amt)}</span></div>
                    ))}
                    {t.is_verlegd && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '12px', color: '#92400e', fontStyle: 'italic' }}><span>BTW verlegd</span><span>€ 0,00</span></div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: '2px solid #1a5276', fontSize: '16px', fontWeight: 'bold', color: '#1a5276' }}><span>Totaal{t.is_verlegd ? '' : ' incl. BTW'}</span><span>{fmtEuro(t.total_incl)}</span></div>
                    </>); })()}
                  </div>
                </div>
                {(previewInvoice.btw_status || '').toLowerCase() === 'verlegd' && (
                  <div style={{ padding: '10px 14px', background: '#fffbeb', border: '1px solid #f59e0b', borderRadius: '4px', fontSize: '12px', marginBottom: '15px', color: '#78350f' }}>
                    <strong>BTW verlegd.</strong> Overeenkomstig artikel 12, vijfde lid van de Wet op de omzetbelasting 1968 is de heffing van btw verlegd naar de afnemer.
                  </div>
                )}
                <div style={{ padding: '15px', background: '#f8f9fa', borderRadius: '4px', fontSize: '12px', marginBottom: '20px' }}>
                  {previewInvoice.notes || 'We verzoeken u vriendelijk het bovenstaande bedrag voor de genoemde vervaldatum te voldoen op onze bankrekening onder vermelding van het factuurnummer.'}
                </div>
                <PaymentPlanBlock plan={previewInvoice.payment_plan} totalIncl={previewInvoice.total_incl} documentNumber={previewInvoice.number} documentType="Factuur" />
                <StandardTermsBlock />
                <div style={{ borderTop: '1px solid #ddd', paddingTop: '15px', fontSize: '11px', color: '#666', display: 'flex', justifyContent: 'space-between' }}>
                  <div><p>{COMPANY.name} · {COMPANY.address}, {COMPANY.city}</p><p>Mobiel: {COMPANY.phone} · E-mail: {COMPANY.email}</p></div>
                  <div style={{ textAlign: 'right' }}><p>KVK: {COMPANY.kvk} · BTW-id: {COMPANY.btw_id}</p><p>IBAN: {COMPANY.iban} · BIC: {COMPANY.bic}</p></div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* LIST */}
      {loading ? (
        <p className="text-gray-500 text-center py-8">Laden...</p>
      ) : invoices.length === 0 ? (
        <Card className="bg-white shadow-sm border-gray-200"><CardContent className="py-12 text-center"><FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" /><p className="text-gray-500">Geen facturen aangemaakt</p></CardContent></Card>
      ) : (
        <div className="space-y-3">
          {/* Search & Status Filter */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Input
                type="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Zoek op nummer, klantnaam, adres of e-mail..."
                className="pl-3 pr-9 bg-white border-gray-200"
                data-testid="invoice-search-input"
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700" data-testid="invoice-search-clear">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[200px] bg-white border-gray-200" data-testid="invoice-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle statussen</SelectItem>
                <SelectItem value="draft">Concept</SelectItem>
                <SelectItem value="sent">Verzonden</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="betaald">Betaald</SelectItem>
                <SelectItem value="overdue">Verlopen</SelectItem>
                <SelectItem value="credit">Credit</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {(() => {
            const term = searchTerm.trim().toLowerCase();
            const filtered = invoices.filter((inv) => {
              if (statusFilter !== 'all') {
                if (statusFilter === 'credit') {
                  if (inv.type !== 'credit') return false;
                } else if ((inv.status || 'draft') !== statusFilter) {
                  return false;
                }
              }
              if (!term) return true;
              return (
                (inv.number || '').toLowerCase().includes(term) ||
                (inv.client_name || '').toLowerCase().includes(term) ||
                (inv.client_address || '').toLowerCase().includes(term) ||
                (inv.client_email || '').toLowerCase().includes(term) ||
                (inv.description || '').toLowerCase().includes(term)
              );
            });
            if (!filtered.length) {
              return <Card className="bg-white shadow-sm border-gray-200"><CardContent className="py-10 text-center"><p className="text-gray-500">Geen facturen gevonden voor je zoekopdracht.</p></CardContent></Card>;
            }
            return filtered.map(inv => (
            <Card key={inv.id} className={`bg-white shadow-sm hover:border-gray-300 transition-all ${inv.type === 'credit' ? 'border-l-4 border-l-red-500' : 'border-gray-200'}`} data-testid={`invoice-${inv.number}`}>
              <CardContent className="py-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-6 flex-wrap">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className={`font-bold ${inv.type === 'credit' ? 'text-red-600' : 'text-[#1a5276]'}`}>{inv.number}</p>
                        {inv.type === 'credit' && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">CREDIT</span>}
                        {(inv.status === 'betaald' || inv.mollie_payment?.status === 'paid') && (
                          <span
                            className="text-xs bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded font-semibold flex items-center gap-1"
                            title={
                              inv.mollie_payment?.paid_at ? `Betaald op ${new Date(inv.mollie_payment.paid_at).toLocaleString('nl-NL')} (Mollie)`
                              : inv.manual_paid_at ? `Handmatig gemarkeerd op ${new Date(inv.manual_paid_at).toLocaleString('nl-NL')}`
                              : 'Betaald'
                            }
                            data-testid={`paid-badge-${inv.id}`}
                          >
                            <Check className="w-3 h-3" /> Betaald
                          </span>
                        )}
                        {inv.mollie_payment?.status === 'open' && inv.status !== 'betaald' && (
                          <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded font-semibold flex items-center gap-1" title="Restbedrag staat open">
                            € Restbedrag open
                          </span>
                        )}
                        {(inv.mollie_payment?.status === 'expired' || inv.mollie_payment?.status === 'failed' || inv.mollie_payment?.status === 'canceled') && inv.status !== 'betaald' && (
                          <span className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded font-semibold flex items-center gap-1" title={`Betaling ${inv.mollie_payment.status}`}>
                            € {inv.mollie_payment.status}
                          </span>
                        )}
                        <ReminderBadge inv={inv} />
                        {inv.down_payment?.status === 'paid' && !inv.mollie_payment && inv.status !== 'betaald' && (
                          <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded font-medium flex items-center gap-1" title={`${inv.down_payment_label || 'Aanbetaling'} van ${inv.down_payment_percentage}% reeds voldaan`}>
                            ✓ {inv.down_payment_label || 'Aanbetaling'}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-400">{inv.invoice_date}</p>
                      {inv.credits_invoice && <p className="text-xs text-red-500">credit voor {inv.credits_invoice}</p>}
                      {inv.credited_by && <p className="text-xs text-orange-500">gecrediteerd door {inv.credited_by}</p>}
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">{inv.client_name}</p>
                      <p className="text-xs text-gray-500">{inv.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <p className={`text-lg font-bold ${inv.type === 'credit' ? 'text-red-600' : 'text-[#3da5a5]'}`}>{fmtEuro(getInvoiceTotals(inv).total_incl)}</p>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => setPreviewInvoice(inv)} className="text-blue-600" data-testid={`preview-invoice-${inv.id}`} title="Bekijken"><Eye className="w-4 h-4" /></Button>
                      {!readOnly && (<>
                      <Button variant="ghost" size="sm" onClick={() => handleOpenEmail(inv)} className="text-[#1a5276] hover:bg-blue-50" data-testid={`email-invoice-${inv.id}`} title="E-mail versturen"><Mail className="w-4 h-4 mr-1" /><span className="text-xs">Versturen</span></Button>
                      <Button variant="ghost" size="sm" onClick={() => setEmailHistoryInvoice(inv)} className="text-gray-500 hover:bg-gray-100" data-testid={`email-history-invoice-${inv.id}`} title="E-mailgeschiedenis"><Clock className="w-4 h-4" /></Button>
                      <ReminderMenu inv={inv} onUpdated={fetchInvoices} />
                      {inv.client_email && (
                        <Button variant="ghost" size="sm" onClick={() => handleOpenReview(inv)}
                          className={inv.review_requested_at ? 'text-amber-500 hover:bg-amber-50' : 'text-gray-500 hover:bg-amber-50 hover:text-amber-600'}
                          title={inv.review_requested_at ? `Review gevraagd op ${formatDateNL(inv.review_requested_at.slice(0, 10))} — opnieuw versturen kan` : 'Vraag klant om een Google review'}
                          data-testid={`request-review-${inv.id}`}>
                          <Star className={`w-4 h-4 ${inv.review_requested_at ? 'fill-amber-400' : ''}`} />
                        </Button>
                      )}
                      {inv.mollie_payment?.payment_id && inv.mollie_payment.status !== 'paid' && (
                        <Button variant="ghost" size="sm" onClick={() => handleSyncPayment(inv)} className="text-amber-600 hover:bg-amber-50" title="Status met Mollie synchroniseren" data-testid={`sync-payment-${inv.id}`}>↻</Button>
                      )}
                      {inv.type !== 'credit' && (
                        <Button variant="ghost" size="sm" onClick={() => handleCreateCredit(inv)} className="text-red-600 hover:bg-red-50" title="Creditfactuur maken" data-testid={`credit-invoice-${inv.id}`}>
                          <RotateCcw className="w-4 h-4 mr-1" /><span className="text-xs">Credit</span>
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(inv)} className="text-gray-600" title="Bewerken"><Edit className="w-4 h-4" /></Button>
                      {inv.status !== 'betaald' && inv.type !== 'credit' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleMarkPaid(inv)}
                          className="text-green-700 hover:text-green-800 hover:bg-green-50 border border-green-200"
                          title="Markeer als betaald"
                          data-testid={`mark-paid-invoice-${inv.id}`}
                        >
                          <Euro className="w-4 h-4 mr-1" /><span className="text-xs font-semibold">Betaald</span>
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleTestPaidNotification(inv)}
                        className="text-purple-700 hover:text-purple-800 hover:bg-purple-50 border border-purple-200"
                        title="Test 'betaald' bevestigings-e-mail naar uw admin-inbox (alsof klant via Mollie heeft betaald)"
                        data-testid={`test-paid-notif-${inv.id}`}
                      >
                        <Mail className="w-4 h-4 mr-1" /><span className="text-xs">Test admin</span>
                      </Button>
                      {inv.client_email && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleTestKlantBedankt(inv)}
                          className="text-amber-700 hover:text-amber-800 hover:bg-amber-50 border border-amber-200"
                          title="Test 'Bedankt voor uw betaling' e-mail naar de klant (preview)"
                          data-testid={`test-klant-bedankt-${inv.id}`}
                        >
                          <Mail className="w-4 h-4 mr-1" /><span className="text-xs">Test klant</span>
                        </Button>
                      )}
                      <StatusActionDropdown
                        type="invoice"
                        id={inv.id}
                        currentStatus={inv.status}
                        axiosInstance={axiosInstance}
                        onUpdated={fetchInvoices}
                      />
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(inv.id)} className="text-red-500" title="Verwijderen"><Trash2 className="w-4 h-4" /></Button>
                      </>)}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ));
          })()}
        </div>
      )}

      <EmailSendModal
        open={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        documentType="invoice"
        document={previewInvoice}
        pdfNode={printRef.current}
        onSent={fetchInvoices}
      />

      <EmailHistoryModal
        open={!!emailHistoryInvoice}
        onClose={() => setEmailHistoryInvoice(null)}
        documentType="invoice"
        documentId={emailHistoryInvoice?.id}
        documentNumber={emailHistoryInvoice?.number}
      />

      <ImportDocumentModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        documentType="invoice"
        onSaved={() => { fetchInvoices(); }}
      />

      {reviewInvoice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4" style={{ zIndex: 9999 }} onClick={() => setReviewInvoice(null)} data-testid="review-modal">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2"><Star className="w-5 h-5 text-amber-400 fill-amber-400" /> Google review vragen</h3>
              <Button variant="ghost" size="sm" onClick={() => setReviewInvoice(null)}><X className="w-4 h-4" /></Button>
            </div>
            <p className="text-sm text-gray-600">
              Stuurt een vriendelijke bedank-mail naar <strong>{reviewInvoice.client_name}</strong> ({reviewInvoice.client_email}) met een knop naar je Google review-pagina.
            </p>
            <div>
              <Label className="text-gray-700">Google review-link</Label>
              <Input value={reviewUrl} onChange={(e) => setReviewUrl(e.target.value)} placeholder="https://g.page/r/..." className="bg-gray-50 border-gray-200" data-testid="review-url-input" />
              <p className="text-xs text-gray-400 mt-1">Te vinden via Google Bedrijfsprofiel → "Vraag om reviews". Wordt onthouden voor de volgende keer.</p>
            </div>
            {reviewInvoice.review_requested_at && (
              <p className="text-xs text-amber-600">⚠️ Al gevraagd op {formatDateNL(reviewInvoice.review_requested_at.slice(0, 10))} — opnieuw versturen kan.</p>
            )}
            <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer bg-gray-50 border border-gray-200 rounded-lg p-3">
              <input type="checkbox" checked={reviewAuto} onChange={(e) => setReviewAuto(e.target.checked)} className="w-4 h-4 mt-0.5" data-testid="review-auto-checkbox" />
              <span>
                <strong>Automatisch versturen</strong>
                <span className="block text-xs text-gray-500 mt-0.5">Zodra een eindfactuur op betaald staat, krijgt de klant het review-verzoek automatisch mee in de betalingsbevestiging (niet bij aanbetalingen of tussenfacturen).</span>
              </span>
            </label>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleSaveReviewOnly} className="flex-1 border-gray-300" data-testid="save-review-settings-btn">
                Alleen opslaan
              </Button>
              <Button onClick={handleSendReview} disabled={reviewSending} className="flex-1 bg-[#3da5a5] hover:bg-[#2d8585]" data-testid="send-review-btn">
                {reviewSending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />} Nu versturen
              </Button>
            </div>
          </div>
        </div>
      )}
      </>)}
    </div>
  );
};

export default Invoices;
